import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, TextField, Button, Chip,
  MenuItem, Alert, Slider, FormControl, InputLabel, Select,
} from '@mui/material';
import { School, ArrowBack } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import knowledgeService from '../../services/knowledgeService';
import eventsService from '../../services/eventsService';
import { colors } from '../../theme/theme';

const TOPIC_TAGS = [
  'Engineering', 'Design', 'Marketing', 'Finance', 'Startups', 'AI/ML',
  'Product', 'Leadership', 'Cloud/DevOps', 'Data Science', 'Cybersecurity', 'Blockchain',
  'Healthcare', 'Legal', 'Education', 'Real Estate', 'Photography', 'Creative Arts',
];

export default function CreateKnowledgeSessionPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [zones, setZones] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    topic_tags: [] as string[],
    knowledge_fee: 20,
    min_capacity: 1,
    max_capacity: 3,
    scheduled_date: '',
    scheduled_time: '18:00',
    duration_minutes: 60,
    zone_id: '',
    voucher_tier: 'standard',
  });

  useEffect(() => {
    eventsService.getZones().then(setZones).catch(() => { /* no-op */ });
  }, []);

  const toggleTag = (tag: string) => {
    setForm(f => ({
      ...f,
      topic_tags: f.topic_tags.includes(tag)
        ? f.topic_tags.filter(t => t !== tag)
        : f.topic_tags.length < 3 ? [...f.topic_tags, tag] : f.topic_tags,
    }));
  };

  const totalPrice = form.knowledge_fee + (form.voucher_tier === 'premium' ? 13 : 10);

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.scheduled_date || !form.zone_id) {
      setError('Please fill in all required fields'); return;
    }
    if (form.topic_tags.length === 0) {
      setError('Select at least one topic tag'); return;
    }
    setError(''); setLoading(true);
    try {
      await knowledgeService.create(form);
      setSuccess(true);
    } catch (e: any) {
      console.warn('[CreateKnowledgeSessionPage] create session failed', e);
      setError(e.message || 'Failed to create session');
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <School sx={{ fontSize: 64, color: colors.primary.main, mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('pages.knowledge.create_success')}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Your knowledge session is pending admin approval. You'll be notified once it's live.
        </Typography>
        <Button variant="contained" onClick={() => navigate('/knowledge')}>Browse Sessions</Button>
        <Button sx={{ ml: 2 }} onClick={() => navigate('/knowledge/my')}>My Sessions</Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/knowledge')} sx={{ mb: 2 }}>Back</Button>

      <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>{t('pages.knowledge.create_title')}</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>{t('pages.knowledge.create_subtitle')}</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <TextField fullWidth label="Session Title *" placeholder="e.g., Modern SRE Practices in Fintech"
          value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          sx={{ mb: 2 }} />

        <TextField fullWidth label="Description *" multiline rows={4}
          placeholder="What will participants learn? What's your background? What should they prepare?"
          value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          sx={{ mb: 3 }} />

        {/* Topic Tags */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Topic Tags (select up to 3) *</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 3 }}>
          {TOPIC_TAGS.map(tag => (
            <Chip key={tag} label={tag} size="small"
              onClick={() => toggleTag(tag)}
              sx={{
                bgcolor: form.topic_tags.includes(tag) ? colors.primary.main : colors.neutral[100],
                color: form.topic_tags.includes(tag) ? 'white' : colors.neutral[600],
                '&:hover': { opacity: 0.8 },
              }} />
          ))}
        </Box>

        {/* Knowledge Fee */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Knowledge Fee: <strong>{form.knowledge_fee} AZN</strong>
        </Typography>
        <Slider
          value={form.knowledge_fee} min={5} max={50} step={5}
          onChange={(_, v) => setForm(f => ({ ...f, knowledge_fee: v as number }))}
          marks={[
            { value: 5, label: '5' }, { value: 15, label: '15' },
            { value: 25, label: '25' }, { value: 35, label: '35' }, { value: 50, label: '50' },
          ]}
          sx={{ mb: 1, color: colors.primary.main }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block' }}>
          Student pays: {totalPrice} AZN ({form.voucher_tier === 'premium' ? 13 : 10} coffee + {form.knowledge_fee} knowledge fee).
          Platform takes 15% of knowledge fee after your 3-month free period.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Schedule & Logistics</Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField fullWidth type="date" label="Date *" InputLabelProps={{ shrink: true }}
            value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
          <TextField fullWidth type="time" label="Time *" InputLabelProps={{ shrink: true }}
            value={form.scheduled_time} onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))} />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Duration</InputLabel>
            <Select value={form.duration_minutes} label="Duration"
              onChange={e => setForm(f => ({ ...f, duration_minutes: Number(e.target.value) }))}>
              <MenuItem value={45}>45 min</MenuItem>
              <MenuItem value={60}>60 min</MenuItem>
              <MenuItem value={90}>90 min</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Zone *</InputLabel>
            <Select value={form.zone_id} label="Zone *"
              onChange={e => setForm(f => ({ ...f, zone_id: e.target.value }))}>
              {zones.map(z => <MenuItem key={z.id} value={z.id}>{z.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Min Students</InputLabel>
            <Select value={form.min_capacity} label="Min Students"
              onChange={e => setForm(f => ({ ...f, min_capacity: Number(e.target.value) }))}>
              {[1, 2, 3].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Max Students</InputLabel>
            <Select value={form.max_capacity} label="Max Students"
              onChange={e => setForm(f => ({ ...f, max_capacity: Number(e.target.value) }))}>
              {[1, 2, 3].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <FormControl fullWidth size="small">
          <InputLabel>Coffee Tier</InputLabel>
          <Select value={form.voucher_tier} label="Coffee Tier"
            onChange={e => setForm(f => ({ ...f, voucher_tier: e.target.value }))}>
            <MenuItem value="standard">Standard (10 AZN)</MenuItem>
            <MenuItem value="premium">Premium (13 AZN)</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Price Summary */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: colors.primary.subtle, borderRadius: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Price Summary (what students pay)</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Coffee voucher ({form.voucher_tier})</Typography>
          <Typography variant="body2">{form.voucher_tier === 'premium' ? 13 : 10} AZN</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Knowledge fee</Typography>
          <Typography variant="body2">{form.knowledge_fee} AZN</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">Service fee</Typography>
          <Typography variant="body2" color="text.secondary">1 AZN</Typography>
        </Box>
        <Box sx={{ borderTop: '1px solid', borderColor: colors.neutral[300], mt: 1, pt: 1, display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ fontWeight: 700 }}>Total</Typography>
          <Typography sx={{ fontWeight: 700, color: colors.primary.main }}>{totalPrice + 1} AZN</Typography>
        </Box>
      </Paper>

      <Button fullWidth variant="contained" size="large" onClick={handleSubmit} disabled={loading}
        sx={{ py: 1.5 }}>
        {loading ? 'Submitting...' : 'Submit for Approval'}
      </Button>
    </Container>
  );
}
